import { APP_ID, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { environment } from '../environments/environment';
import { RouterModule, Routes } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';

// Material
import { MaterialModule } from './modules/shared/material/material.module';
// Swiper
import { SwiperModule } from 'swiper/angular';
import { SwiperComponent } from 'swiper/angular';

// CDK
import { LayoutModule } from '@angular/cdk/layout';
import { DragDropModule } from '@angular/cdk/drag-drop';

// App
import { AppComponent } from './app.component';
import { FooterComponent } from './common/footer/footer.component';
import { BannerCarouselComponent } from './banner-carousel/banner-carousel.component';
import { MainComponent } from './main/main.component';
import { HeroComponent } from '@/common/hero/hero.component';
import { MainHeaderComponent } from '@/main/main-header/main-header.component';
import { MainStandardComponent } from '@/main/sections/main-standard/main-standard.component';
import { MainHighlightedComponent } from '@/main/sections/main-highlighted/main-highlighted.component';
import { MainCompactComponent } from '@/main/sections/main-compact/main-compact.component';
import { LoaderComponent } from './loader/loader.component';
import { LoaderService } from './loader/loader.service';
import { LoaderInterceptor } from './loader/loader.interceptor';
import { OfferBoxMobileHighlightComponent } from '@/main/offer-boxes/offer-box-mobile-highlight/offer-box-mobile-highlight.component';
import { OfferBoxMobileHorizontalComponent } from '@/main/offer-boxes/offer-box-mobile-horizontal/offer-box-mobile-horizontal.component';
import { MainHorizontalComponent } from '@/main/sections/main-horizontal/main-horizontal.component';
import { AvailableBoardsDialogComponent } from '@/main/offer-boxes/offer-box/available-boards-dialog/available-boards-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { DatePipe, NgOptimizedImage, registerLocaleData } from '@angular/common';
import { ButtonModule } from './modules/shared/button/button.module';
import { PipesModule } from './modules/shared/pipes/pipes.module';
import { CustomSelectComponent } from './common/custom-select/custom-select.component';
import { SuccessFooterDialogComponent } from './common/footer/success-footer-dialog/success-footer-dialog.component';
import { CustomHostRouteGuard } from '@/common/custom-host-route/custom-host-route.guard';
import { OfferGuard } from './offer/offer.guard';
import { MainGuard } from '@/main/main-guard/main.guard';
import { NotFoundComponent } from './common/not-found/not-found.component';
import { OfferUrlBuilderService } from './common/offer-url-builder.service';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { currentOfferReducer } from './_store/current-offer/current-offer.reducer';
import { CurrentOfferEffects } from './_store/current-offer/current-offer.effects';
import { HTTP_CLIENT_SSR_TIMEOUT, HttpTimeoutInterceptor } from './interceptors/timeout.interceptor';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { generalReducer } from './_store/general/general.reducer';
import { NewTopSearchModule } from '@/new-top-search/new-top-search.module';
import { offersReducer } from '@/_store/offers/offers.reducer';
import { OffersEffects } from '@/_store/offers/offers.effects';
import { SharedModule } from './shared.module';
import { metaReducers } from './_store/meta-reducer';
import { getInitialAppState } from './_store/get-initial-app-state';
import { API_URL_TOKEN } from '@oskar-frontend/comfort-package';

import localePl from '@angular/common/locales/pl';
import { MobileWrapperComponent } from './common/mobile-wrapper/mobile-wrapper.component';
import { GeneralDataEffects } from './_store/general/general.effects';
import { NaviComponent } from '@/new-navi/navi/navi.component';
registerLocaleData(localePl);

const routes: Routes = [
	{
		path: '',
		component: MainComponent,
		pathMatch: 'full',
		canActivate: [MainGuard],
	},
	{
		path: 'wakacje-autokarem/:slug/:slug',
		loadChildren: () => import('./offer/new-offer-list/offer-list.module').then((m) => m.OfferListModule),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: 'wakacje-autokarem',
		loadChildren: () => import('./offer/new-offer-list/offer-list.module').then((m) => m.OfferListModule),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: 'ulubione',
		loadChildren: () => import('./offer/new-offer-list/offer-list.module').then((m) => m.OfferListModule),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: 'rezerwacja',
		loadChildren: () => import('./reservation-form/reservation-form.module').then((m) => m.ReservationFormModule),
	},
	{
		path: 'rezerwacja/podsumowanie/:hash',
		loadChildren: () =>
			import('./reservation-form/reservation-form-summary/reservation-form-summary.module').then(
				(m) => m.ReservationFormSummaryModule,
			),
	},
	{
		path: 'rezerwacja/uczestnicy/:hash',
		loadComponent: () =>
			import('./update-reservation-data/update-reservation-data.component').then(
				(c) => c.UpdateReservationDataComponent,
			),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: 'rezerwacja/zwroty/:hash',
		loadComponent: () =>
			import('./update-refund-data/update-refund-data.component').then((c) => c.UpdateRefundDataComponent),
		canActivate: [CustomHostRouteGuard],
	},

	{
		path: 'oferta/:productKind/:locations/:name/:id',
		loadChildren: () => import('./offer/offer.module').then((m) => m.OfferModule),
		canActivate: [OfferGuard],
	},
	{
		path: 'oferta/:id',
		loadChildren: () => import('./offer/offer.module').then((m) => m.OfferModule),
		canActivate: [OfferGuard],
	},
	{
		path: 'login',
		loadComponent: () => import('./auth/login/login.component').then((mod) => mod.LoginComponent),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: 'kontakt',
		loadComponent: () => import('./contact-page/contact-page.component').then((mod) => mod.ContactPageComponent),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: 'strefa-klienta',
		loadChildren: () => import('./client-portal/client-portal.routes').then((m) => m.CLIENT_PORTAL_ROUTES),
		canActivate: [CustomHostRouteGuard],
	},
	{
		path: '**',
		pathMatch: 'full',
		component: NotFoundComponent,
	},
];

@NgModule({
	declarations: [
		AppComponent,
		BannerCarouselComponent,
		MainComponent,
		NotFoundComponent,
		LoaderComponent,
		AvailableBoardsDialogComponent,
		CustomSelectComponent,
		SuccessFooterDialogComponent,
	],
	bootstrap: [AppComponent],
	exports: [RouterModule],
	imports: [
		RouterModule.forRoot(routes, {
			initialNavigation: 'enabledBlocking',
		}),
		BrowserAnimationsModule,
		PipesModule,
		ButtonModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: environment.production,
			// Register the ServiceWorker as soon as the application is stable
			// or after 30 seconds (whichever comes first).
			registrationStrategy: 'registerWhenStable:30000',
		}),
		LayoutModule,
		FormsModule,
		ReactiveFormsModule,
		DragDropModule,
		SwiperModule,
		MatDialogModule,
		NgOptimizedImage,
		NewTopSearchModule,
		HeroComponent,
		MaterialModule,
		SharedModule,
		StoreModule.forRoot(
			{ currentOffer: currentOfferReducer, general: generalReducer, offers: offersReducer },
			{ metaReducers, initialState: getInitialAppState },
		),
		EffectsModule.forRoot([CurrentOfferEffects, OffersEffects, GeneralDataEffects]),
		StoreDevtoolsModule.instrument({
			maxAge: 50,
			logOnly: environment.production,
		}),
		FooterComponent,
		NaviComponent,
		MainHeaderComponent,
		MainCompactComponent,
		MainHorizontalComponent,
		MainStandardComponent,
		MainHighlightedComponent,
		OfferBoxMobileHorizontalComponent,
		OfferBoxMobileHighlightComponent,
		MobileWrapperComponent,
	],
	providers: [
		SwUpdate,
		DatePipe,
		HttpClient,
		SwiperComponent,
		LoaderService,
		{
			provide: HTTP_CLIENT_SSR_TIMEOUT,
			useValue: 5000,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpTimeoutInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoaderInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: TokenInterceptor,
			multi: true,
		},
		{
			provide: API_URL_TOKEN,
			useValue: environment.apiUrl,
		},
		CustomHostRouteGuard,
		MainGuard,
		OfferUrlBuilderService,
		{ provide: APP_ID, useValue: 'serverApp' },
		{ provide: LOCALE_ID, useValue: 'pl-PL' },
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}
